/**
 * ResubmitClaimDialog Component
 *
 * Confirmation dialog for creating a correction (code 7) or void (code 8) claim.
 */

import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';

interface ResubmitClaimDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (originalRefNumber?: string) => void;
  mode: 'correction' | 'void';
  isSubmitting: boolean;
  defaultRefNumber?: string;
}

const DIALOG_CONFIG = {
  correction: {
    title: 'Create Correction',
    description:
      'This will create a new draft claim as a correction (replacement) of the original. The original claim will remain unchanged. You can edit the new claim before submitting it.',
    confirmLabel: 'Create Correction',
  },
  void: {
    title: 'Void Claim',
    description:
      'This will create a new draft claim that voids the original. The void claim will cancel the original when submitted to the payer.',
    confirmLabel: 'Create Void',
  },
} as const;

export const ResubmitClaimDialog: React.FC<ResubmitClaimDialogProps> = ({
  open,
  onClose,
  onConfirm,
  mode,
  isSubmitting,
  defaultRefNumber,
}) => {
  const [refNumber, setRefNumber] = useState(defaultRefNumber || '');
  const config = DIALOG_CONFIG[mode];

  const handleConfirm = () => {
    onConfirm(refNumber || undefined);
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{config.title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {config.description}
        </DialogContentText>
        <TextField
          label="Payer Reference Number"
          helperText="Original payer/clearinghouse claim reference number (Box 22). Optional — will be auto-filled from the original if available."
          value={refNumber}
          onChange={e => setRefNumber(e.target.value)}
          fullWidth
          size="small"
          disabled={isSubmitting}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={mode === 'void' ? 'error' : 'primary'}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting ? 'Creating...' : config.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
